import ApiWithLogging from '../utils/apiWithLogging';
import { Device, DeviceFormData, DeviceStatus } from '../types/device';

// Helper function for safe number conversion
const validateNumber = (value: string | number | undefined): number | undefined => {
  if (value === undefined || value === null || value === '') return undefined;
  const num = Number(value);
  return isNaN(num) ? undefined : num;
};

// พารามิเตอร์สำหรับการดึงข้อมูลอุปกรณ์
export interface GetDevicesParams {
  page?: number;
  limit?: number;
  search?: string;
  device_type_id?: number;
  status?: DeviceStatus;
  brand?: string;
  brand_id?: number;
  model?: string;
  is_k_connect_enabled?: boolean;
  // true = เฉพาะเครื่องที่ติดตามราย Serial Number · false = เฉพาะของนับจำนวน · ไม่ส่ง = ทั้งหมด
  // (ใช้กับโฟลว์ที่ทำงานรายเครื่อง เช่น เลือกเครื่องใหม่ตอนเปลี่ยนอุปกรณ์)
  is_serialized?: boolean;
  // FB-2026-0020: ลิงก์จากหน้า "สรุปคงเหลือ" (คลิกตัวเลข) กรองรุ่น master
  device_model_id?: number;
  // FB-2026-0020: true = หยิบได้จริง · false = พร้อมใช้งานแต่ติดล็อก (ลิงก์ "พร้อมใช้"/"รอปลดล็อก")
  pickable?: boolean;
}

// การตอบกลับจากการดึงข้อมูลอุปกรณ์
export interface GetDevicesResponse {
  items: Device[];
  total: number;
  page: number;
  limit: number;
  // Low stock นับทั้งระบบทุกหน้า (ไม่ผูกกับ filter/pagination) — optional กัน deploy skew backend เก่า
  low_stock_total?: number;
  low_stock_items?: Device[];
}

// === Types สำหรับ Device Autocomplete (Phase 1: Maintenance Enhancement) ===

export interface RentalContextInfo {
  id: number;
  code?: string;
  status?: string;
}

export interface CompanyContextInfo {
  id: number;
  code?: string;
  name: string;
}

export interface SiteContextInfo {
  id: number;
  code?: string;
  name: string;
  activation_code?: string; // รหัส Activate (ใช้ค้นหา/ยืนยันโครงการ)
  customer_code?: string;   // รหัสลูกค้า (legacy K-Visit) — ใช้เมื่อโครงการยังไม่มีรหัส Activate
}

// สินค้า/ระบบของอุปกรณ์ในการเช่าล่าสุด (ใช้ filter ประเภทการซ่อมตามสินค้า)
export interface ProductContextInfo {
  id: number;
  code?: string;
  name: string;
}

export interface DeviceTypeInfo {
  id: number;
  code: string;
  name: string;
}

export interface BrandInfo {
  id: number;
  code: string;
  name: string;
}

export interface DeviceModelInfo {
  id: number;
  code: string;
  name: string;
  brand_id: number;
}

export interface DeviceAutocompleteItem {
  id: number;
  code?: string;
  serial_number?: string;
  model?: string;
  status?: string;
  device_type?: DeviceTypeInfo;
  brand?: BrandInfo;
  device_model?: DeviceModelInfo;
  has_active_rental: boolean;
  rental?: RentalContextInfo;
  company?: CompanyContextInfo;
  site?: SiteContextInfo;
  product?: ProductContextInfo;
  display_label: string;
  display_context?: string;
}

export interface DeviceAutocompleteResponse {
  items: DeviceAutocompleteItem[];
  total: number;
}

// แปลงข้อมูลฟอร์มเป็นรูปแบบที่ backend ต้องการ
// export เพื่อให้ vitest คุมกติกา "ไม่ส่งฟิลด์ที่ผู้เรียกไม่ได้ระบุ" ได้ (C8)
export const convertFormToApiData = (data: DeviceFormData) => {
  return {
    code: data.code,
    device_type_id: data.device_type_id,
    brand_id: data.brand_id,
    model: data.model,
    // feedback ทิวากร 2026-09-08: converter เคยตกฟิลด์นี้ทั้งที่ฟอร์มมี dropdown "รุ่น" อยู่แล้ว
    // → อุปกรณ์ที่สร้างจากฟอร์มทีละชิ้นได้ device_model_id = NULL ทุกตัว (prod 21 เครื่องตั้งแต่
    // 25 ส.ค. · ที่มี FK ครบ 808 เครื่องมาจาก bulk import ซึ่งไม่ผ่าน converter นี้)
    // ผลหนักที่สุดอยู่ที่ของนับจำนวน: ตัวจับคู่ "ของประเภทเดียวกัน" ใช้ FK นี้ พอ NULL == NULL
    // ของคนละรุ่นเลยถูกรวมยอดเข้าแถวเดียว (A06(5G) + A064G)
    // C8: ส่งเฉพาะเมื่อผู้เรียกระบุมาจริง — ไม่แตะ = ไม่มี key = exclude_unset คงค่าเดิม
    ...(data.device_model_id !== undefined ? { device_model_id: data.device_model_id || null } : {}),
    serial_number: data.serial_number || null,
    // C8 (MANUAL_AUDIT_SYSTEM_FINDINGS): imei / warranty_expire_date ไม่มีช่องกรอกในฟอร์ม
    // อุปกรณ์เลย แต่เดิม converter ยัด `|| null` ลง payload เสมอ → กดแก้ไขเรื่องอื่นแล้ว
    // ค่าเดิมถูกล้างเป็น NULL (พิสูจน์บน dev 3 ก.ย. 2026: แก้แค่ "หมายเหตุ" แล้ว IMEI หาย
    // — มีเครื่องที่มี IMEI จริงอยู่ 348 เครื่องที่เสี่ยงโดนแบบนี้)
    // ส่งเฉพาะเมื่อผู้เรียก "ระบุมาจริง" → ไม่ส่ง key = DeviceUpdate ใช้ exclude_unset คงค่าเดิม
    // (แนวเดียวกับ anydesk_* ด้านล่างที่ถูกถอดออกจาก payload ด้วยเหตุผลเดียวกัน)
    ...(data.imei !== undefined ? { imei: data.imei || null } : {}),
    status: data.status,
    purchase_date: data.purchase_date || null,
    ...(data.warranty_expire_date !== undefined
      ? { warranty_expire_date: data.warranty_expire_date || null }
      : {}),
    notes: data.notes || null,
    location: data.location || null,

    // Inventory management fields
    is_serialized: data.is_serialized !== undefined ? data.is_serialized : true,
    quantity: data.quantity !== undefined ? data.quantity : 1,
    unit: data.unit || "เครื่อง",
    minimum_quantity: data.minimum_quantity !== undefined ? data.minimum_quantity : 0,

    // Procurement/Asset fields - ข้อมูลการจัดซื้อ/ครุภัณฑ์
    // C8 pattern: ส่งเฉพาะเมื่อผู้เรียก "ระบุมาจริง" — ผู้ใช้ไม่แตะ = ไม่มี key = exclude_unset
    // คงค่าเดิม · ป้องกันการเขียนทับข้อมูลจัดซื้อของเครื่องเก่า 683/799 เครื่องบน prod ที่ค่าว่าง
    // ⚠️ `purchase_price` ห้ามใช้ `|| null` หรือ `|| 0` เด็ดขาด — `0` เป็นราคาที่ถูกต้อง
    // (ของแถม/รับโอน) ถ้า coerce ทิ้งจะกลายเป็นบั๊กเดียวกับ monthly_fee ของซิม แต่หนักกว่า
    // เพราะ "0 บาท" ดูเหมือนข้อมูลจริง แยกไม่ออกจากของแท้ในรายงานมูลค่าทรัพย์สิน
    ...(data.supplier_id !== undefined ? { supplier_id: data.supplier_id ?? null } : {}),
    ...(data.purchase_price !== undefined ? { purchase_price: data.purchase_price ?? null } : {}),
    invoice_number: data.invoice_number || null,
    receive_date: data.receive_date || null,
    condition: data.condition || null,
    book_value: data.book_value !== undefined ? data.book_value : null,
    asset_number: data.asset_number || null,

    // Remote Access (AnyDesk) อยู่ที่ rental_items — ฟอร์มอุปกรณ์ไม่ส่ง/ไม่แก้ field นี้
    // แก้ผ่าน remoteAccessService (PUT /devices/{id}/remote-access → รายการเช่าของสัญญาที่เครื่องอยู่)

    // New fields for data migration
    location_note: data.location_note || null,
    device_number: data.device_number !== undefined ? data.device_number : null
  };
};

// บริการจัดการข้อมูลอุปกรณ์
const deviceService = {
  // ดึงรายการอุปกรณ์ทั้งหมด
  async getDevices(params: GetDevicesParams = {}): Promise<GetDevicesResponse> {
    // ใช้ค่าเริ่มต้นถ้าไม่มีการระบุ
    const queryParams = {
      page: params.page || 1,
      limit: params.limit || 10,
      search: params.search,
      device_type_id: params.device_type_id,
      status: params.status,
      brand: params.brand,
      brand_id: params.brand_id,
      model: params.model,
      is_k_connect_enabled: params.is_k_connect_enabled,
      is_serialized: params.is_serialized,
      device_model_id: params.device_model_id,
      pickable: params.pickable,
    };

    const response = await ApiWithLogging.get<GetDevicesResponse>('/devices/',
      { params: queryParams },
      { 
        service: 'deviceService',
        action: 'view_devices_list',
        entityType: 'device'
      }
    );
    
    return response;
  },

  // ดึงข้อมูลอุปกรณ์ตาม id
  async getDevice(id: number): Promise<Device> {
    const response = await ApiWithLogging.get<Device>(`/devices/${id}`, {},
      {
        service: 'deviceService',
        action: 'view_device_detail',
        entityType: 'device',
        entityId: id
      }
    );
    return response;
  },


  // สร้างอุปกรณ์ใหม่
  async createDevice(data: DeviceFormData): Promise<Device> {
    // แปลงข้อมูลจาก frontend format เป็น backend format
    const deviceData = convertFormToApiData(data);
    
    const response = await ApiWithLogging.post<Device>('/devices/', deviceData, {},
      {
        service: 'deviceService',
        action: 'create_device',
        entityType: 'device',
        details: {
          code: data.code,
          brand_id: data.brand_id,
          model: data.model,
          serial_number: data.serial_number
        }
      }
    );
    return response;
  },

  // สร้างอุปกรณ์หลายเครื่องพร้อมกันจากรายการ serial (สำหรับทีม stock) — ATOMIC
  async createDevicesBulk(data: any): Promise<{ created_count: number; devices: Array<{ id: number; code?: string; serial_number?: string }> }> {
    const response = await ApiWithLogging.post<{ created_count: number; devices: Array<{ id: number; code?: string; serial_number?: string }> }>(
      '/devices/bulk',
      data,
      {},
      {
        service: 'deviceService',
        action: 'bulk_create_devices',
        entityType: 'device',
        details: { count: Array.isArray(data?.serial_numbers) ? data.serial_numbers.length : undefined }
      }
    );
    return response;
  },

  // แก้ไขข้อมูลอุปกรณ์
  // BUG-S162-P1 rollout (S413): ส่ง updated_at ที่ฟอร์มเห็นล่าสุดเป็น `ifMatch`
  // → backend ปฏิเสธ stale write ด้วย 409 แทนการเขียนทับสถานะที่ flow อื่นเพิ่งตั้งไว้
  async updateDevice(id: number, data: Partial<DeviceFormData>, ifMatch?: string): Promise<Device> {
    // แปลงข้อมูลจาก frontend format เป็น backend format
    const deviceData = convertFormToApiData(data as DeviceFormData);
    
    const response = await ApiWithLogging.put<Device>(`/devices/${id}`, deviceData, ifMatch ? { headers: { 'If-Match': ifMatch } } : {},
      {
        service: 'deviceService',
        action: 'update_device',
        entityType: 'device',
        entityId: id,
        details: {
          updated_fields: Object.keys(data),
          code: data.code,
          status: data.status
        }
      }
    );
    return response;
  },

  // ลบอุปกรณ์
  async deleteDevice(id: number): Promise<Device> {
    const response = await ApiWithLogging.delete<Device>(`/devices/${id}`, {},
      {
        service: 'deviceService',
        action: 'delete_device',
        entityType: 'device',
        entityId: id
      }
    );
    return response;
  },

  // ส่งออกข้อมูลเป็น Excel
  async exportToExcel(params: GetDevicesParams = {}): Promise<Blob> {
    const response = await ApiWithLogging.get('/devices/export/excel',
      {
        params,
        responseType: 'blob'
      },
      {
        service: 'deviceService',
        action: 'export_devices_excel',
        entityType: 'device',
        details: { exportParams: params }
      }
    );

    return response;
  },

  // ดึงอุปกรณ์ที่พร้อมใช้งาน
  async getAvailableDevices(): Promise<Device[]> {
    // BUG-S592-AVAILCAP: เดิมไม่ส่ง param เลย → backend ใช้ default limit=100 ทั้งที่มีอุปกรณ์ว่าง 685
    // ตัว (เรียงตาม id DESC) → picker เลือกเครื่องรหัสเก่าไม่ได้เลย · 1000 = เพดาน le ของ endpoint
    // BUG-S2207-002: หน้าเดียว 1000 ไม่พอ (dev มีเครื่องว่าง 1,379 · เช่น 201-V006 หลุดจาก picker สร้าง/แก้สัญญา)
    // → วนด้วย skip จนได้ชุดที่สั้นกว่า limit (เพดาน 20 หน้า กันวนไม่รู้จบ)
    const limit = 1000;
    const all: Device[] = [];
    for (let page = 0; page < 20; page++) {
      const batch = await ApiWithLogging.get<Device[]>('/devices/available', { params: { skip: page * limit, limit } },
        {
          service: 'deviceService',
          action: 'view_available_devices',
          entityType: 'device'
        }
      );
      const list = Array.isArray(batch) ? batch : [];
      all.push(...list);
      if (list.length < limit) break;
    }
    return all;
  },

  // === Device Autocomplete Functions (Phase 1: Maintenance Enhancement) ===

  // ค้นหาอุปกรณ์สำหรับ autocomplete พร้อม rental context
  async searchDevicesForAutocomplete(
    query: string,
    limit: number = 10
  ): Promise<DeviceAutocompleteResponse> {
    const response = await ApiWithLogging.get<DeviceAutocompleteResponse>(
      '/devices/search/autocomplete',
      {
        params: { q: query, limit }
      },
      {
        service: 'deviceService',
        action: 'search_devices_autocomplete',
        entityType: 'device',
        details: { query, limit }
      }
    );
    return response;
  },

  // ดึงข้อมูลอุปกรณ์พร้อม rental context
  async getDeviceWithContext(deviceId: number): Promise<DeviceAutocompleteItem> {
    const response = await ApiWithLogging.get<DeviceAutocompleteItem>(
      `/devices/${deviceId}/with-context`,
      {},
      {
        service: 'deviceService',
        action: 'get_device_with_context',
        entityType: 'device',
        entityId: deviceId
      }
    );
    return response;
  }
};

// Named exports สำหรับ React Query hooks
export const getDevices = deviceService.getDevices;
export const getDevice = deviceService.getDevice;
export const createDevice = deviceService.createDevice;
export const updateDevice = deviceService.updateDevice;
export const deleteDevice = deviceService.deleteDevice;
export const getAvailableDevices = deviceService.getAvailableDevices;
export const searchDevicesForAutocomplete = deviceService.searchDevicesForAutocomplete;
export const getDeviceWithContext = deviceService.getDeviceWithContext;

export default deviceService;